import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { 
  FileText, 
  Calendar, 
  Users, 
  BarChart3, 
  Mail, 
  ExternalLink,
  Settings,
  Download
} from "lucide-react";

const ProgramDashboard = () => {
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Program Dashboard</h1>
        <p className="text-gray-600">Manage your Rent Readiness Education Program</p>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-gray-800">24</p>
                <p className="text-sm text-gray-600">Active Participants</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <FileText className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-gray-800">12</p>
                <p className="text-sm text-gray-600">New Applications</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <Calendar className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-gray-800">8</p>
                <p className="text-sm text-gray-600">Scheduled Meetings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <BarChart3 className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-gray-800">85%</p>
                <p className="text-sm text-gray-600">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tool Integration Sections */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        
        {/* Jotform Integration */}
        <Card className="border-2 border-dashed border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span>Jotform Integration</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Manage participant intake forms, applications, and assessments
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-gray-800">Setup Instructions:</p>
                <ol className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>1. Create Jotform account (free)</li>
                  <li>2. Build intake form using template</li>
                  <li>3. Get embed code and add below</li>
                </ol>
              </div>
              <Button variant="outline" className="w-full">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Jotform Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Calendly Integration */}
        <Card className="border-2 border-dashed border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <span>Calendly Integration</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Schedule consultations, check-ins, and program sessions
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-gray-800">Setup Instructions:</p>
                <ol className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>1. Create Calendly account (free)</li>
                  <li>2. Set up meeting types</li>
                  <li>3. Get embed code for website</li>
                </ol>
              </div>
              <Button variant="outline" className="w-full">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Calendly Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Airtable Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <span>Participant Tracker</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Live participant data from your Rent Readiness Program
            </p>
            <div className="bg-gray-50 rounded-lg p-1">
              <iframe 
                className="airtable-embed w-full rounded-lg" 
                src="https://airtable.com/embed/appWZlrSFLAAbLNYm/shrktQfk2tlK5RJgp" 
                frameBorder="0" 
                width="100%" 
                height="533" 
                style={{background: "transparent", border: "1px solid #ccc"}}
              ></iframe>
            </div>
            <div className="mt-4 flex space-x-2">
              <Button variant="outline" size="sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Full Airtable
              </Button>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mailchimp Integration */}
        <Card className="border-2 border-dashed border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-orange-600" />
              <span>Mailchimp Integration</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Automated communications and program updates
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-gray-800">Automations:</p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Welcome emails</li>
                  <li>• Program reminders</li>
                  <li>• Progress updates</li>
                  <li>• Resource sharing</li>
                </ul>
              </div>
              <Button variant="outline" className="w-full">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Mailchimp Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Quick Actions</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Button className="h-auto p-4 flex flex-col items-center space-y-2">
              <Download className="w-6 h-6" />
              <span>Export Participant Data</span>
            </Button>
            <Button variant="outline" className="h-auto p-4 flex flex-col items-center space-y-2">
              <FileText className="w-6 h-6" />
              <span>Generate Reports</span>
            </Button>
            <Button variant="outline" className="h-auto p-4 flex flex-col items-center space-y-2">
              <Mail className="w-6 h-6" />
              <span>Send Program Update</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Integration Status */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">🚀 Next Steps</h3>
        <p className="text-blue-700 mb-4">
          Set up your program management tools to unlock the full potential of your education program:
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="font-semibold text-blue-800">Priority 1 (This Week):</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>✓ Create Jotform account</li>
              <li>✓ Set up Calendly scheduling</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-semibold text-blue-800">Priority 2 (Next Week):</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Set up Airtable tracking</li>
              <li>• Configure Mailchimp automation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDashboard;